<script setup lang="ts">
/**
 * Экран «Заменить продукт»: сетка альтернатив для одного слота комбо
 * с доплатой относительно текущего выбора.
 *
 * Одноимённые товары разных размеров схлопнуты в одну карточку — размер
 * выбирается уже в карточке слота.
 */

import type { ComboGroupItem } from '@entities/product';
import { menuUnavailableLabel } from '@entities/product';
import type { ComboSlot, ComboVariantFamily } from '@features/combo';

const props = defineProps<{
  comboSlot: ComboSlot;
  /** Доплата за товар относительно выбранного сейчас */
  priceDelta: (product: ComboGroupItem) => number;
  placeholderImg?: string;
}>();

const emit = defineEmits<{
  (e: 'select', productId: number): void;
  (e: 'selectUnavailable', product: ComboGroupItem): void;
  (e: 'close'): void;
}>();

const imageOf = (family: ComboVariantFamily) =>
  family.products.find((p) => p.img?.[0]?.url)?.img?.[0]?.url || props.placeholderImg || null;

/**
 * Товар семейства, который получит пользователь при замене.
 * Размер сохраняем: выбирал 25 см — получит 25 см нового блюда.
 */
const targetProduct = (family: ComboVariantFamily): ComboGroupItem => {
  const currentSize = props.comboSlot.product?.size;
  // Размер сохраняем, только если он доступен: иначе подставится вариант, который
  // сервер всё равно отобьёт.
  const available = family.products.filter((p) => p.is_available !== false);
  return available.find((p) => p.size === currentSize) ?? family.cheapest;
};

/** Как и у товаров: недоступность нужно сказать явно, отсутствие флага — «доступно» */
const isFamilyAvailable = (family: ComboVariantFamily) => family.isAvailable !== false;

const isSelected = (family: ComboVariantFamily) =>
  isFamilyAvailable(family) && family.products.some((p) => p.id === props.comboSlot.product?.id);

const formatDelta = (delta: number) => {
  if (delta === 0) return '';
  const rounded = Math.round(delta);
  return rounded > 0 ? `+ ${rounded} ₽` : `− ${Math.abs(rounded)} ₽`;
};

/** Причину берём с первого товара семейства — у всех размеров она одна и та же */
const unavailableLabel = (family: ComboVariantFamily) => menuUnavailableLabel(family.products[0]);

/** Подпись порции того товара, который реально уедет в корзину при замене */
const portionLabel = (family: ComboVariantFamily) => targetProduct(family).portion_label ?? null;

/**
 * Недоступную карточку НЕ делаем `disabled`: браузер тогда съедает клик, и гость просто
 * тычет в мёртвую плитку. Клик принимаем, выбор не меняем и отдаём наверх — конструктор
 * объясняет тостом, почему товар взять нельзя.
 */
const handleClick = (family: ComboVariantFamily) => {
  if (!isFamilyAvailable(family)) {
    emit('selectUnavailable', family.products[0]);
    return;
  }
  emit('select', targetProduct(family).id);
};
</script>

<template>
  <div class="combo-picker" data-testid="combo-replace-picker">
    <div class="combo-picker__header">
      <button type="button" class="combo-picker__back" aria-label="Назад" @click="emit('close')">
        <UIcon name="i-heroicons-chevron-left" class="w-6 h-6" />
      </button>
      <h3 class="combo-picker__title">Заменить продукт</h3>
    </div>

    <div class="combo-picker__grid">
      <button
        v-for="family in comboSlot.families"
        :key="family.key"
        type="button"
        class="combo-picker__card"
        :class="{
          'combo-picker__card--selected': isSelected(family),
          'combo-picker__card--unavailable': !isFamilyAvailable(family),
        }"
        :aria-disabled="!isFamilyAvailable(family)"
        :data-testid="isFamilyAvailable(family) ? 'combo-family' : 'combo-family-unavailable'"
        @click="handleClick(family)"
      >
        <div class="combo-picker__image-wrap">
          <img
            v-if="imageOf(family)"
            :src="imageOf(family)!"
            :alt="family.name"
            class="combo-picker__image"
            loading="lazy"
          />
          <div v-else class="combo-picker__image-placeholder" />
        </div>

        <div class="combo-picker__name">{{ family.name }}</div>

        <!-- Порция комбо: «250 г». Без неё гость сравнивает карточки по цене за
             килограмм и не понимает, за что платит. -->
        <div v-if="portionLabel(family)" class="combo-picker__portion">
          {{ portionLabel(family) }}
        </div>

        <div class="combo-picker__delta">
          <!-- У недоступного вместо доплаты — причина: цена тут уже не важна -->
          <span
            v-if="!isFamilyAvailable(family)"
            class="combo-picker__badge combo-picker__badge--unavailable"
          >
            {{ unavailableLabel(family) }}
          </span>
          <span
            v-else-if="!isSelected(family) && formatDelta(priceDelta(targetProduct(family)))"
            class="combo-picker__badge"
          >
            {{ formatDelta(priceDelta(targetProduct(family))) }}
          </span>
        </div>

        <!-- Галочка 1:1 как у добавок в ProductAddons: inline SVG, а не иконка
             из набора — lucide в проекте локально не установлен. -->
        <span v-if="isSelected(family)" class="combo-picker__check">
          <svg class="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
            <path
              fill-rule="evenodd"
              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
              clip-rule="evenodd"
            />
          </svg>
        </span>
      </button>
    </div>

    <div class="combo-picker__footer">
      <button type="button" class="combo-picker__done" @click="emit('close')">Назад в комбо</button>
    </div>
  </div>
</template>

<style scoped lang="scss">
.combo-picker {
  @apply relative h-full flex flex-col min-h-0;

  // Матовое стекло на ::before — на flex-контейнере backdrop-filter ломает
  // расчёт высоты детей в WebKit (см. ComboCustomizePanel).
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    z-index: 0;
    pointer-events: none;
    background-color: rgba(18, 18, 20, 0.72);
    backdrop-filter: blur(28px) saturate(1.4);
    -webkit-backdrop-filter: blur(28px) saturate(1.4);
  }

  & > * {
    position: relative;
    z-index: 1;
  }

  &__header {
    @apply flex items-center gap-2 px-4 pt-4 pb-3 shrink-0;
  }

  &__back {
    @apply w-9 h-9 rounded-full flex items-center justify-center shrink-0;
    @apply bg-white/10 text-white hover:bg-white/20 transition-colors;
  }

  &__title {
    @apply text-lg lg:text-2xl font-bold text-white;
  }

  &__grid {
    @apply flex-1 min-h-0 overflow-y-auto px-4 pb-4;
    @apply grid grid-cols-2 sm:grid-cols-3 gap-3 content-start;
  }

  // Выбор помечаем так же, как добавки в «Изменить состав»: белая карточка
  // с зелёной галочкой, без рамки.
  &__card {
    @apply relative rounded-2xl p-2 text-center transition-all duration-150;
    @apply bg-white/5;

    &--selected {
      @apply bg-white shadow-lg;

      // !important обязателен: у названия есть dark:text-white, и на белой
      // карточке текст сливался бы с фоном в тёмной теме.
      .combo-picker__name {
        @apply text-gray-800 #{!important};
      }
    }

    // Недоступный товар: красная рамка + приглушённая картинка. Рамка внутренняя
    // (ring), иначе карточка в сетке подпрыгивает на 2px относительно соседей.
    &--unavailable {
      @apply ring-2 ring-inset ring-hui-danger bg-hui-danger/5 cursor-not-allowed;

      .combo-picker__image-wrap,
      .combo-picker__name {
        @apply opacity-45;
      }
    }
  }

  &__check {
    @apply absolute top-2 right-2 w-5 h-5 rounded-full bg-hui-success text-white;
    @apply flex items-center justify-center;
  }

  &__image-wrap {
    @apply w-full rounded-xl overflow-hidden mb-2;
    aspect-ratio: 1 / 1;
  }

  &__image {
    @apply w-full h-full object-contain;
  }

  &__image-placeholder {
    @apply w-full h-full bg-gray-700;
  }

  &__name {
    @apply text-sm font-medium text-white leading-tight;
  }

  &__portion {
    @apply text-xs text-white/60 mt-0.5;
  }

  &__delta {
    @apply mt-1 min-h-[22px] flex items-center justify-center;
  }

  &__badge {
    @apply text-xs font-semibold px-2 py-0.5 rounded-full;
    @apply bg-red-500/15 text-red-500;

    // Причина недоступности читается как статус, а не как доплата: заливка плотнее
    &--unavailable {
      @apply bg-hui-danger text-hui-danger-foreground opacity-100;
    }
  }

  &__footer {
    @apply p-4 shrink-0 lg:hidden;
  }

  &__done {
    @apply w-full py-3 rounded-full font-semibold text-white bg-red-500;
    @apply hover:bg-red-600 transition-colors;
  }
}
</style>
